import { html, View } from 'rune-ts';
import klass from './PlusContentCard.module.scss';
import { getResizedUrl } from '../../../../../../modules/Util/Image/S/Function/getResized';
import { AnchorTarget } from '../../../../shared/type/global';
import { MakeOptional } from '../../../../shared/type/types';
import { map, pipe, range, toArray } from '@fxts/core';
import { typo } from '../../../../shared/typography/typo';
import $dataStr from 'fxdom/es/dataStr.js';
import { ProductThumbnailView } from '@services/MarppleShop/renderApp/components/cells/ProductThumbnail/ProductThumbnailView.ts';
import { ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';

export interface ContentProduct {
  id: number;
  name: string;
  thumbnail_ratio: number;
  thumbnails: any;
  base_product_color_face_url?: string;
}

export interface PlusContent {
  id: number;
  thumbnail: string;
  title: string;
  description: string;
  products: ProductCardViewData[];
}

export interface PlusContentCardData {
  content: PlusContent;
}

export interface PlusContentCardState {
  not_lazy: boolean;
  klass_name: string;
  target: AnchorTarget;
  without_item: boolean;
  replace_stack: boolean;
  is_mobile: boolean;
}

export type PlusContentCardOptions = MakeOptional<
  PlusContentCardState,
  'without_item' | 'replace_stack' | 'target' | 'klass_name' | 'not_lazy'
>;

export class PlusContentCard extends View<PlusContentCardData> {
  state: PlusContentCardState;

  constructor(data: PlusContentCardData, options: PlusContentCardOptions) {
    super(data);

    this.state = {
      ...options,
      not_lazy: options.not_lazy ?? true,
      klass_name: options.klass_name ?? '',
      target: options.target ?? '_self',
      without_item: options.without_item ?? false,
      replace_stack: options.replace_stack ?? false,
    };
  }

  override template() {
    const { content } = this.data;
    const { target, is_mobile, klass_name, not_lazy, replace_stack, without_item } = this.state;

    const link = `/${T.lang}/@/todays/${content.id}`;

    const image_url = content.thumbnail.includes('data:image')
      ? content.thumbnail
      : getResizedUrl({
          url: content.thumbnail,
          width: 780,
          format: 'webp',
        });

    const post_message = {
      [replace_stack ? 'replaceNavigate' : 'screen']: {
        type: 'util',
        title: T('mshop::Content Detail'),
        uri: link,
      },
    };

    return html`
      <div class="${klass.plus_content_card} ${klass_name}">
        <div class="${klass.thumb_container}">
          <a href="${link}" target="${target}" data-post-message="${$dataStr(post_message)}">
            <div class="${klass.thumb_wrapper}">
              <img
                class="${!not_lazy ? 'lazy-src' : ''} ${klass.thumbnail}"
                ${not_lazy ? 'src' : 'data-src'}="${image_url}"
                alt="${content.title}"
              />
            </div>
          </a>
        </div>
        <div class="${klass.content}">
          <a href="${link}" target="${target}" data-post-message="${$dataStr(post_message)}">
            <div class="${klass.content_title} ${typo(is_mobile ? '16_bold' : '20_bold')}">
              <p class="${klass.content_title_txt}">${html.preventEscape(content.title) || ''}</p>
            </div>
            <p
              class="${klass.content_description} ${typo('14_medium')} ${content.description
                ? ''
                : klass.empty_content}"
            >
              ${html.preventEscape(content.description) || '_'}
            </p>
          </a>

          ${without_item || (is_mobile && content.products.length == 0)
            ? ''
            : html`<div class="${klass.content_products}">
                ${pipe(
                  range(4),
                  map((idx) => content.products[idx]),
                  map((product) => {
                    const product_link = `/${T.lang}/${product?.domain_name}/products/${product?.id}`;

                    const post_message2 = {
                      screen: {
                        type: 'util',
                        title: T('mshop::상품상세'),
                        uri: product_link,
                      },
                    };

                    const img_alt = product?.name;

                    return html`
                      <a
                        class="${klass.content_product} ${!product ? klass.empty : ''}"
                        ${product ? html`href='${product_link}'` : ''}
                        data-post-message="${$dataStr(post_message2)}"
                      >
                        ${product
                          ? new ProductThumbnailView({
                              thumbnail: product.thumbnail_info,
                              options: {
                                resize_width: 305 * 2,
                                img_alt,
                              },
                            })
                          : html`<div class="${klass.content_empty}"></div>`}
                      </a>
                    `;
                  }),
                  toArray,
                )}
              </div>`}
        </div>
      </div>
    `;
  }
}
